// src/App.tsx
import React, { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ScrollToTop from "./components/common/ScrollToTop";
import Router from "./routes/router";
import apiService from "./services/api.services";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { queryClient } from "./lib/queryClient";

const DevRoleSwitcher = process.env.NODE_ENV === "development"
  ? React.lazy(() => import("./components/dev/DevRoleSwitcher"))
  : null;

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize session on app load
    const initializeApp = async () => {
      try {
        // This checks for existing session (localStorage or cookies)
        await apiService.initializeSession();
        console.log('App session initialized');
      } catch (error) {
        console.error('Failed to initialize session:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, []);

  // Show minimal loading state while initializing
  // This prevents flash of login page for authenticated users
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ImpersonationProvider>
          {/* Smooth scroll reset only on route change */}
          <ScrollToTop smooth={true} excludePaths={["/no-scroll"]} />

          {/* Global toast notifications */}
          <Toaster
            position="top-center"
            expand={true}
            richColors={true}
            closeButton={true}
            duration={4000}
            theme="dark" // 'light', 'dark', or 'system'
          />

          {/* Main routes */}
          <Router />

          {/* Dev-only role switcher — stripped in production */}
          {DevRoleSwitcher && (
            <React.Suspense fallback={null}>
              <DevRoleSwitcher />
            </React.Suspense>
          )}
        </ImpersonationProvider>
      </BrowserRouter>
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}

export default App;